import { WebAnalytics } from './analytics';
import type { ShareContentType } from './analyticsEvents';

const MONGO_OBJECT_ID = /^[0-9a-fA-F]{24}$/;

export type BookingButtonAction = 'book' | 'check_availability';

/**
 * First-touch creator id from stored acquisition params or current URL.
 * Share links set creator_id query param and utm_content to the creator's user id.
 */
export function getReferringCreatorId(
  storedAcquisition: Record<string, unknown> | null,
  searchParams?: URLSearchParams | null,
): string | undefined {
  const fromQuery = searchParams?.get('creator_id');
  if (fromQuery && MONGO_OBJECT_ID.test(fromQuery)) {
    return fromQuery;
  }

  if (!storedAcquisition) {
    return undefined;
  }

  const storedCreatorId = storedAcquisition.creator_id;
  if (typeof storedCreatorId === 'string' && MONGO_OBJECT_ID.test(storedCreatorId)) {
    return storedCreatorId;
  }

  const utmContent = storedAcquisition.utm_content;
  const utmCampaign = storedAcquisition.utm_campaign;
  const utmMedium = storedAcquisition.utm_medium;
  if (typeof utmContent === 'string' && MONGO_OBJECT_ID.test(utmContent)) {
    if (utmCampaign === 'creator_share' || utmMedium === 'share') {
      return utmContent;
    }
  }

  return undefined;
}

/** Resolves referral creator id for API write payloads during share-link sessions. */
export function getReferralCreatorIdForApi(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const storedAcquisition = WebAnalytics.getAcquisitionSource();
  const searchParams = new URLSearchParams(window.location.search);
  return getReferringCreatorId(storedAcquisition, searchParams);
}

/** Maps share redirect resource_type to analytics content_type where possible. */
export function shareResourceTypeToContentType(
  resourceType: string,
): ShareContentType | undefined {
  if (resourceType === 'itinerary') return 'itinerary';
  if (resourceType === 'list') return 'list';
  if (
    resourceType === 'place' ||
    resourceType === 'recommendation' ||
    resourceType === 'location'
  ) {
    return 'place';
  }
  return undefined;
}

/** Extracts a post id from itinerary/list/post share landing paths. */
export function extractPostIdFromSharePath(path: string): string | undefined {
  const match =
    path.match(/\/itinerary\/([0-9a-fA-F]{24})/) ||
    path.match(/\/list\/([0-9a-fA-F]{24})/) ||
    path.match(/\/post\/([0-9a-fA-F]{24})/);
  return match?.[1];
}

export type ShareLinkViewedAnalyticsProps = {
  share_token: string;
  resource_type: string;
  destination_path: string;
  referrer?: string;
  creator_id?: string;
  content_type?: ShareContentType;
  post_id?: string;
  shared_location_id?: string;
  shared_recommendation_id?: string;
};

export function buildShareLinkViewedProps(input: {
  share_token: string;
  resource_type: string;
  destination_path: string;
  referrer?: string;
  creator_id?: string | null;
  shared_location_id?: string | null;
  shared_recommendation_id?: string | null;
}): ShareLinkViewedAnalyticsProps {
  const content_type = shareResourceTypeToContentType(input.resource_type);
  const post_id = extractPostIdFromSharePath(input.destination_path);

  return {
    share_token: input.share_token,
    resource_type: input.resource_type,
    destination_path: input.destination_path,
    referrer: input.referrer,
    ...(input.creator_id ? { creator_id: input.creator_id } : {}),
    ...(content_type ? { content_type } : {}),
    ...(post_id ? { post_id } : {}),
    ...(input.shared_location_id ? { shared_location_id: input.shared_location_id } : {}),
    ...(input.shared_recommendation_id ? { shared_recommendation_id: input.shared_recommendation_id } : {}),
  };
}

export type HighIntentTripAnalyticsProps = {
  button_action: BookingButtonAction;
  source: string;
  locationId?: string | null;
  locationName?: string | null;
  hotelId?: string | null;
  post_id?: string;
  content_type?: ShareContentType;
};

export type ExperienceOutboundAnalyticsProps = {
  surface: string;
  vendor: 'gyg';
  search_query: string;
  cmp: string;
  user_id?: string;
  post_id?: string;
  place_id?: string;
  google_place_id?: string;
  location_name?: string;
  content_type?: 'itinerary' | 'list';
};

export type ExperienceWidgetShownAnalyticsProps = ExperienceOutboundAnalyticsProps & {
  widget_type: 'activities' | 'city';
};

export type HotelWidgetShownSurface =
  | 'place_modal'
  | 'list_timeline_card'
  | 'itinerary_timeline_card';

export type HotelWidgetCtaType = 'book_your_stay' | 'check_availability';

/** Impression when a hotel booking CTA is visible (mirrors Experience Widget Shown). */
export type HotelWidgetShownAnalyticsProps = {
  surface: HotelWidgetShownSurface;
  cta_type: HotelWidgetCtaType;
  vendor?: 'gt_stay';
  user_id?: string;
  post_id?: string;
  place_id?: string;
  google_place_id?: string;
  location_name?: string;
  content_type?: 'itinerary' | 'list';
};

export type Stay22OutboundAnalyticsProps = {
  surface: string;
  vendor: 'stay22';
  cmp: string;
  user_id?: string;
  post_id?: string;
  place_id?: string;
  google_place_id?: string;
  location_name?: string;
  content_type?: 'itinerary' | 'list';
  lat?: number;
  lng?: number;
};

export type Stay22MapAnalyticsProps = Stay22OutboundAnalyticsProps & {
  has_dates: boolean;
};

/** @deprecated Use Stay22MapAnalyticsProps */
export type Stay22MapSheetAnalyticsProps = Stay22MapAnalyticsProps;
